import React, { useEffect, useState } from "react";
import {
    Accordion,
    Alert,
    Box,
    Flex,
    Stack,
    Text,
    TextInput,
} from "@mantine/core";
import { LuCheck, LuSmartphone } from "react-icons/lu";
import useAnalytics from "../hooks/useAnalytics.js";
import { useGetContactInformationQuery } from "../services/api.js";
import {
    getAccordionTileIconStyles,
    getAccordionTileStyles,
} from "./accordionTileStyles.js";

const EMAIL_ERROR = "Enter a valid email address.";
const PHONE_ERROR = "Enter a valid 10-digit mobile phone number.";
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[\d()\-\s+.]+$/;

const emailIsValid = (value: string) =>
    value.length === 0 || EMAIL_PATTERN.test(value.trim());
const phoneIsValid = (value: string) => {
    if (value.length === 0) return true;

    if (!PHONE_PATTERN.test(value)) {
        return false;
    }

    const digitsOnly = value.replace(/\D/g, "");

    return digitsOnly.length === 10;
};

export default function ReachabilityContact() {
    const { data, isLoading, isError } = useGetContactInformationQuery();
    const { trackEvent, AnalyticsEvents } = useAnalytics();
    const [email, setEmail] = useState("");
    const [mobilePhone, setMobilePhone] = useState("");

    useEffect(() => {
        if (data) {
            setEmail(data.email ?? "");
            setMobilePhone(data.mobilePhone ?? "");
        }
    }, [data]);

    const emailValid = emailIsValid(email);
    const mobilePhoneValid = phoneIsValid(mobilePhone);
    const bothMissing = !isLoading && !email && !mobilePhone;
    const onlyEmailMissing = !isLoading && !email && !!mobilePhone;
    const onlyMobilePhoneMissing = !isLoading && !!email && !mobilePhone;
    const editorDisabled = true;

    return (
        <Accordion
            variant="contained"
            mt="xl"
            onChange={(value) => {
                if (value) {
                    trackEvent(AnalyticsEvents.REACHABILITY_TILE_EXPANDED);
                }
            }}
            data-testid="reachability-tile"
            styles={getAccordionTileStyles}
        >
            <Accordion.Item value="reachability">
                <Accordion.Control data-testid="reachability-toggle">
                    <Flex align="center" gap="sm">
                        <Box sx={getAccordionTileIconStyles}>
                            <LuSmartphone color="white" />
                        </Box>
                        <Text fw={700}>Still the Best Way to Reach You</Text>
                    </Flex>
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack spacing="md">
                        {isError && (
                            <Alert color="red" data-testid="contact-information-load-error">
                                We could not load your contact information. Please try
                                again.
                            </Alert>
                        )}

                        {!isError && bothMissing && (
                            <Alert color="blue" data-testid="both-missing-prompt">
                                <Text fw={700}>
                                    Add your email address and mobile phone number so
                                    you do not miss important plan communications.
                                </Text>
                            </Alert>
                        )}
                        {!isError && onlyEmailMissing && (
                            <Alert color="blue" data-testid="email-missing-prompt">
                                Add your email address to receive important plan
                                communications.
                            </Alert>
                        )}
                        {!isError && onlyMobilePhoneMissing && (
                            <Alert color="blue" data-testid="mobile-missing-prompt">
                                Add your mobile phone number to receive important plan
                                communications.
                            </Alert>
                        )}

                        <TextInput
                            label="Email address"
                            placeholder="name@example.com"
                            type="email"
                            autoComplete="email"
                            value={email}
                            disabled={editorDisabled}
                            error={!emailValid && EMAIL_ERROR}
                            rightSection={
                                email.length > 0 && emailValid ? <LuCheck color="green" /> : null
                            }
                            data-testid="email-input"
                        />
                        <TextInput
                            label="Mobile phone number"
                            placeholder="(555) 555-5555"
                            inputMode="tel"
                            autoComplete="tel"
                            value={mobilePhone}
                            disabled={editorDisabled}
                            error={!mobilePhoneValid && PHONE_ERROR}
                            rightSection={
                                mobilePhone.length > 0 && mobilePhoneValid
                                    ? <LuCheck color="green" />
                                    : null
                            }
                            data-testid="mobile-phone-input"
                        />
                        <Box>
                            <Text size="xs">
                                Your contact information helps CareSource send important
                                coverage, benefit, and renewal communications.
                            </Text>
                        </Box>
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>
        </Accordion>
    );
}